import React from 'react';
import { NotificationDto, NotificationType } from '../../types/notifications';
import styles from './NotificationPanel.module.css';

interface NotificationItemProps {
    notification: NotificationDto;
    onRead: (id: number) => void;
    onDelete: (id: number) => void;
}

const getRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const getNotificationIcon = (type: NotificationType): string => {
    switch (type) {
        case NotificationType.NewBusinessCreated:
            return '🏪';
        case NotificationType.NewUserRegistered:
            return '👤';
        case NotificationType.NewAppointment:
            return '📅';
        case NotificationType.AppointmentCancelled:
            return '❌';
        case NotificationType.NewStaffJoined:
            return '👥';
        default:
            return '📢';
    }
};

export const NotificationItem: React.FC<NotificationItemProps> = ({
    notification,
    onRead,
    onDelete
}) => {
    const handleClick = () => {
        if (!notification.isRead) {
            onRead(notification.id);
        }
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        onDelete(notification.id);
    };

    return (
        <div
            className={`${styles.notificationItem} ${notification.isRead ? styles.read : styles.unread}`}
            onClick={handleClick}
        >
            <div className={styles.notificationIcon}>
                {getNotificationIcon(notification.type)}
            </div>
            <div className={styles.notificationContent}>
                <div className={styles.notificationTitle}>{notification.title}</div>
                {notification.message && (
                    <div className={styles.notificationMessage}>{notification.message}</div>
                )}
                <div className={styles.notificationTime}>{getRelativeTime(notification.createdAt)}</div>
            </div>
            <button
                className={styles.deleteButton}
                onClick={handleDelete}
                aria-label="Delete notification"
            >
                ✕
            </button>
        </div>
    );
};

export default NotificationItem;
